import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import { getJSON, postJSON, type MessageResponse, type UserProfile } from '../lib/api';
import { buildPaymentPasswordRotatePayload } from '../lib/paymentPasswordRotate';
import { clearMpcUnlockCache } from '../lib/mpcUnlockCache';
import { sha256Utf8Hex } from '../lib/passwordWire';
import { appMainClass, narrowPanelError, pageWrapClass } from '../layout/page';
import { inputStackFieldClass, primaryButtonBlockClass, secondaryDarkButtonClass } from '../components/form/fieldClasses';

export default function UserSettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const [newLoginPwd, setNewLoginPwd] = useState('');
  const [payNew, setPayNew] = useState('');
  const [payConfirm, setPayConfirm] = useState('');
  const [payCur, setPayCur] = useState('');
  const [showReturnToWallet, setShowReturnToWallet] = useState(false);
  const [payMfa, setPayMfa] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { res, data } = await getJSON<UserProfile>('/api/user/profile');
      if (res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (res.status === 403) {
        navigate('/home', { replace: true });
        return;
      }
      if (!res.ok) {
        setProfile(null);
        setMsg(data.error || t('common.error'));
        setMsgOk(false);
        return;
      }
      setProfile({
        username: data.username,
        has_payment_password: data.has_payment_password,
        mfa_enabled: data.mfa_enabled,
        mpc_stub: data.mpc_stub,
        mpc_enabled: data.mpc_enabled,
        mpc_server_repo_note: data.mpc_server_repo_note,
        mpc_client_repo_note: data.mpc_client_repo_note,
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    void load();
  }, [load, i18n.language]);

  async function onSaveLoginPassword(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    setShowReturnToWallet(false);
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/user/change-password', {
        new_password: await sha256Utf8Hex(newLoginPwd),
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || t('settings.loginPwdSaved'));
      setNewLoginPwd('');
    } finally {
      setBusy(false);
    }
  }

  async function onSavePaymentPassword(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    setShowReturnToWallet(false);
    setBusy(true);
    try {
      if (!profile) {
        return;
      }
      const firstTime = !profile.has_payment_password;
      if (firstTime && payNew !== payConfirm) {
        setMsgOk(false);
        setMsg(t('settings.payMismatch'));
        return;
      }
      const body: Record<string, unknown> = { payment_password: await sha256Utf8Hex(payNew) };
      if (profile.mfa_enabled) {
        body.mfa_code = payMfa.trim();
      }
      let rotatedWalletIds: number[] = [];
      if (profile.has_payment_password) {
        body.current_payment_password = await sha256Utf8Hex(payCur);
        const wallets = await buildPaymentPasswordRotatePayload(payCur.trim(), payNew.trim(), t);
        body.wallets = wallets;
        rotatedWalletIds = wallets.map((w) => w.wallet_id);
      }
      const { res, data } = await postJSON<MessageResponse>('/api/user/payment-password', body);
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      for (const id of rotatedWalletIds) {
        sessionStorage.removeItem(`cointrader_mpc_sess:${id}`);
        sessionStorage.removeItem(`cointrader_mpc_blob:${id}`);
      }
      clearMpcUnlockCache();
      setMsgOk(true);
      setShowReturnToWallet(firstTime);
      setMsg(firstTime ? t('settings.payPwdSavedFirst') : data.message || t('settings.payPwdSaved'));
      setPayNew('');
      setPayConfirm('');
      setPayCur('');
      setPayMfa('');
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  if (!profile) {
    return (
      <div className={pageWrapClass}>
        <main className={narrowPanelError}>
          <p className="text-sm text-red-600">{msg || t('common.error')}</p>
          <Link to="/home" className="mt-4 inline-block text-sm text-blue-600 hover:underline">
            {t('settings.back')}
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/home"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            ← {t('settings.back')}
          </Link>
          <LanguageSelect />
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('settings.title')}</h1>
        <p className="text-sm text-slate-500">{profile.username}</p>

        {msg ? (
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${
              msgOk
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {msg}
            {showReturnToWallet && msgOk ? (
              <p className="mt-3 font-normal">
                <Link to="/home" className="text-blue-700 underline hover:text-blue-900">
                  {t('settings.back')}
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}

        {profile.has_payment_password ? (
          <>
            <section className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">{t('settings.loginPwdTitle')}</h2>
              <p className="mt-1 text-sm text-slate-600">{t('settings.loginPwdHint')}</p>
              <form className="mt-4 grid gap-3" onSubmit={(e) => void onSaveLoginPassword(e)}>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.newLoginPwdPh')}
                  value={newLoginPwd}
                  onChange={(e) => setNewLoginPwd(e.target.value)}
                />
                <button type="submit" disabled={busy} className={secondaryDarkButtonClass}>
                  {t('settings.saveLoginPwd')}
                </button>
              </form>
            </section>

            <section className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">{t('settings.payTitle')}</h2>
              <p className="mt-1 text-sm text-slate-600">{t('settings.payIntro')}</p>
              <form className="mt-4 grid gap-3" onSubmit={(e) => void onSavePaymentPassword(e)}>
                <input
                  required
                  type="password"
                  autoComplete="current-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.payCurrentPh')}
                  value={payCur}
                  onChange={(e) => {
                    setPayCur(e.target.value);
                    setShowReturnToWallet(false);
                  }}
                />
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.payNewPh')}
                  value={payNew}
                  onChange={(e) => {
                    setPayNew(e.target.value);
                    setShowReturnToWallet(false);
                  }}
                />
                {profile.mfa_enabled ? (
                  <input
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={inputStackFieldClass}
                    placeholder={t('mfa.code')}
                    value={payMfa}
                    onChange={(e) => setPayMfa(e.target.value)}
                  />
                ) : null}
                <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
                  {t('settings.savePayPwd')}
                </button>
              </form>
            </section>
          </>
        ) : (
          <>
            <section className="rounded-2xl border border-amber-200/90 bg-amber-50/80 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-amber-950">{t('settings.payTitle')}</h2>
              <p className="mt-2 text-sm text-amber-950/90">{t('settings.payFirstBanner')}</p>
              <p className="mt-2 text-sm text-amber-950/85">{t('settings.payIntroFirst')}</p>
              <form className="mt-4 grid gap-3" onSubmit={(e) => void onSavePaymentPassword(e)}>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.paySetPh')}
                  value={payNew}
                  onChange={(e) => {
                    setPayNew(e.target.value);
                    setShowReturnToWallet(false);
                  }}
                />
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.payConfirmPh')}
                  value={payConfirm}
                  onChange={(e) => {
                    setPayConfirm(e.target.value);
                    setShowReturnToWallet(false);
                  }}
                />
                {profile.mfa_enabled ? (
                  <input
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={inputStackFieldClass}
                    placeholder={t('mfa.code')}
                    value={payMfa}
                    onChange={(e) => setPayMfa(e.target.value)}
                  />
                ) : null}
                <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
                  {t('settings.savePayPwdFirst')}
                </button>
              </form>
            </section>

            <section className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">{t('settings.loginPwdTitle')}</h2>
              <p className="mt-1 text-sm text-slate-600">{t('settings.loginPwdHint')}</p>
              <form className="mt-4 grid gap-3" onSubmit={(e) => void onSaveLoginPassword(e)}>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  className={inputStackFieldClass}
                  placeholder={t('settings.newLoginPwdPh')}
                  value={newLoginPwd}
                  onChange={(e) => setNewLoginPwd(e.target.value)}
                />
                <button type="submit" disabled={busy} className={secondaryDarkButtonClass}>
                  {t('settings.saveLoginPwd')}
                </button>
              </form>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
